// analyses.js — v0.7.0 (Saved Analyses)
//
// Thin data-access layer for the `analyses` table. Keeps Supabase / SQL
// shape out of the UI components: pages call saveAnalysis / listAnalyses /
// deleteAnalysis and get back the same { data, error } shape that supabase-js
// uses everywhere else in this codebase.
//
// Row Level Security (migration 013) restricts every row to its owner —
// the anon key is what ships in the browser, RLS is what protects the data.
// The INSERT policy requires user_id = auth.uid(), so saveAnalysis pulls the
// current user from the live session before writing.

import { supabase } from './supabaseClient';

const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

function notConfigured() {
  return Promise.resolve(NOT_CONFIGURED);
}

// Defensive number coercion — analyze-property may surface strings, nulls,
// or NaN depending on which code path produced the value. Postgres numeric
// columns won't accept undefined, and we'd rather store NULL than 0.
function toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function toText(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

/**
 * Persist a completed analysis for the current user.
 *
 * @param {object} input  - The form / request payload that produced `result`.
 *                          Address fields (formatted_address, street, city,
 *                          state, zip, place_id) and lat/lng/latitude/longitude
 *                          are read off this object.
 * @param {object} result - The full analyze-property response. Stored as
 *                          jsonb; key fields are also denormalized into
 *                          dedicated columns so the list view can sort/filter
 *                          without parsing JSON on every row.
 */
export async function saveAnalysis(input, result, options = {}) {
  if (!supabase) return notConfigured();

  // RLS insert policy requires user_id = auth.uid(). Pull it from the live
  // session rather than trusting a caller-supplied id.
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData?.user) {
    return {
      data: null,
      error: userError || { message: 'You must be signed in to save an analysis.' },
    };
  }

  // DEF-011 — result.subjectAddress is now a STRING (the formatted address).
  // The parts (street/city/state/zip/place_id) live on result.input.address,
  // which the backend normalizes into an object. Read each from its real home.
  const subjectInput = result?.input || {};
  const subjectAddrObj = subjectInput.address || {};
  const subjectFormatted =
    typeof result?.subjectAddress === "string" ? result.subjectAddress : null;

  const row = {
    user_id: userData.user.id,

    // Subject address — prefer the values echoed back in the result, fall
    // back to the form-provided input so a re-run from a saved analysis
    // still has full provenance.
    formatted_address:
      toText(subjectFormatted) ||
      toText(subjectAddrObj.formatted_address) ||
      toText(input?.formatted_address) ||
      toText(input?.address) ||
      null,
    street: toText(input?.street) || toText(subjectAddrObj.street),
    city: toText(input?.city) || toText(subjectAddrObj.city),
    state: toText(input?.state) || toText(subjectAddrObj.state),
    zip: toText(input?.zip) || toText(subjectAddrObj.zip),
    place_id: toText(input?.place_id) || toText(subjectAddrObj.place_id),

    lat: toNum(input?.latitude ?? input?.lat ?? subjectInput.latitude ?? subjectInput.lat),
    lng: toNum(input?.longitude ?? input?.lng ?? subjectInput.longitude ?? subjectInput.lng),

    // Full result blob — the source of truth for re-run / detail views.
    result,

    // Denormalized highlights for the list view. Postgres numeric is happy
    // with JS numbers; NULL when the value is missing or non-finite.
    dscr: toNum(result?.financials?.dscr),
    deal_score: toText(result?.dealScore),
    recommendation: toText(result?.recommendation?.decision),
    monthly_revenue: toNum(result?.revenue?.monthlyGross),
    noi: toNum(result?.financials?.noi),
  };

  const insertResult = await supabase.from('analyses').insert(row).select().single();

  // DEF-014 — when this save was launched from a lead (Run Analysis →
  // DealAnalyzer), mirror the row into lead_analyses so the parent lead's
  // "Saved Analyses" section (DEF-015) picks it up. Linkage failure is
  // NON-FATAL: the analysis is already safely persisted on `analyses`, so
  // we log a warning and return the original success — the UI still shows
  // "Saved ✓". RLS (migration 021) allows authenticated users to insert
  // their own rows; user_id check happens server-side via with-check.
  const { leadId } = options;
  if (leadId && !insertResult.error && insertResult.data) {
    const inserted = insertResult.data;
    const { error: linkError } = await supabase.from('lead_analyses').insert({
      lead_id:         leadId,
      analysis_id:     inserted.id,
      user_id:         userData.user.id,
      snapshot:        result,
      dscr:            inserted.dscr,
      deal_score:      inserted.deal_score,
      recommendation:  inserted.recommendation,
      monthly_revenue: inserted.monthly_revenue,
      noi:             inserted.noi,
    });
    if (linkError) {
      // eslint-disable-next-line no-console
      console.warn(
        '[saveAnalysis] analysis saved but lead linkage failed:',
        linkError.message
      );
    }
  }

  return insertResult;
}

/**
 * List the current user's saved analyses, newest first.
 * RLS scopes the result automatically — no need to filter by user_id.
 */
export async function listAnalyses() {
  if (!supabase) return notConfigured();
  return supabase
    .from('analyses')
    .select('*')
    .order('created_at', { ascending: false });
}

/**
 * Read all lead_analyses rows owned by the current user for a given set of
 * lead ids — single round trip. Returns rows newest-first; RLS scopes to
 * the signed-in user automatically. Used by the Leads page to populate
 * each LeadCard's "Saved Analyses" section in one query (DEF-015).
 */
export async function listLeadAnalyses(leadIds) {
  if (!supabase) return notConfigured();
  if (!Array.isArray(leadIds) || leadIds.length === 0) {
    return { data: [], error: null };
  }
  return supabase
    .from('lead_analyses')
    .select('id, lead_id, analysis_id, created_at, recommendation, dscr, deal_score, monthly_revenue, noi')
    .in('lead_id', leadIds)
    .order('created_at', { ascending: false });
}

/**
 * Delete a saved analysis by id. RLS prevents deleting another user's rows
 * even if an id from elsewhere is somehow supplied.
 */
export async function deleteAnalysis(id) {
  if (!supabase) return notConfigured();
  return supabase.from('analyses').delete().eq('id', id);
}

/**
 * BL-048 (ADR-068) — owner-scoped read of a single analysis by id for the
 * stable /my-analyses/:analysisId route. RLS returns only the caller's row, so
 * a foreign/unauthorized id resolves to "no rows" (PGRST116) → treated as a
 * safe not-found by the caller (never a cross-user disclosure). Serves EVERY
 * authoritative successful analysis (BUY and non-BUY) after a fresh login.
 */
export async function getAnalysisById(id) {
  if (!supabase) return notConfigured();
  if (!id || typeof id !== 'string') {
    return { data: null, error: { code: 'invalid_id', message: 'A valid analysis id is required.' } };
  }
  return supabase.from('analyses').select('*').eq('id', id).single();
}
